import 'css/src/components/common/custom-player/VideoPlayer';

import React, { useEffect, useRef, useState } from 'react';

import MaterialSvgIcon from 'js/src/components/common/custom-player/SvgIcon';
import { getFormattedTimer } from 'js/src/libs/custom-utils';

export enum OverlayButtonVariant {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

type Props = {
    src: string;
    poster?: string;
    overlayButtonVariant?: OverlayButtonVariant;
    videoElementRef?: React.RefObject<HTMLVideoElement>;
};

const VideoPlayer = React.memo(function VideoPlayer({
    src,
    poster,
    overlayButtonVariant = OverlayButtonVariant.PRIMARY,
    videoElementRef = undefined,
}: Props) {
    const localVideoElmRef = useRef<HTMLVideoElement>(null);
    // Use passed video element ref if available, otherwise use local ref.
    const videoElmRef = videoElementRef || localVideoElmRef;

    const [enableControls, setEnableControls] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [hasPlaybackEnded, setHasPlaybackEnded] = useState(false);
    const [videoDuration, setVideoDuration] = useState(0);
    const [currentDuration, setCurrentDuration] = useState(0);

    const seekStep = 0.01;
    const playBackIconName = isPlaying ? 'pause' : 'play_arrow';

    // Set Total Duration
    useEffect(() => {
        const videoInstance = videoElmRef.current;
        if (!videoInstance) {
            return;
        }
        videoInstance.addEventListener('loadedmetadata', handleMetaDataUpdate);
        videoInstance.addEventListener('timeupdate', handleTimeUpdate);
        videoInstance.addEventListener('ended', handleVideoEnded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect to mute/unmute the video.
    useEffect(() => {
        if (!videoElmRef.current) {
            return;
        }
        videoElmRef.current.muted = isMuted;
    }, [isMuted, videoElmRef]);

    const handleMetaDataUpdate = () => {
        if (!videoElmRef.current) {
            return;
        }
        setVideoDuration(videoElmRef.current.duration);
        setEnableControls(true);
    };

    const handleTimeUpdate = () => {
        if (!videoElmRef.current) {
            return;
        }
        setCurrentDuration(parseFloat(videoElmRef.current.currentTime.toFixed(2)));
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
        setCurrentDuration(videoDuration);
        if (videoElmRef.current) {
            videoElmRef.current.currentTime = videoDuration;
            videoElmRef.current.pause();
        }
        setHasPlaybackEnded(true);
    };

    const isCurrentlyPlaying = () => {
        if (videoElmRef.current) {
            return !(videoElmRef.current.paused || videoElmRef.current.ended);
        } else {
            return false;
        }
    };

    const onPlayPause = () => {
        if (isCurrentlyPlaying()) {
            setIsPlaying(false);
            videoElmRef.current?.pause();
        } else {
            setIsPlaying(true);
            videoElmRef.current?.play();
        }
    };

    const pauseVideo = (evt: React.MouseEvent<HTMLInputElement>) => {
        if (isCurrentlyPlaying()) {
            setIsPlaying(false);
            videoElmRef.current?.pause();
        }
        setCurrentDuration(parseInt(evt.currentTarget.value));
    };

    const handleSeekVideo = (
        evt: React.MouseEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (!videoElmRef.current) {
            return;
        }
        let currentTime = Number(evt.currentTarget.value);
        currentTime = parseFloat(currentTime.toFixed(2));
        videoElmRef.current.currentTime = currentTime;
        setCurrentDuration(currentTime);

        if (!isCurrentlyPlaying()) {
            setIsPlaying(true);
            videoElmRef.current.play();
        }
        setHasPlaybackEnded(false);
    };

    const handleVideoReplay = () => {
        if (!videoElmRef.current) {
            return;
        }
        videoElmRef.current.currentTime = 0;
        setHasPlaybackEnded(false);
        setIsPlaying(true);
        videoElmRef.current.play();
    };

    const toggleVolume = () => {
        setIsMuted(!isMuted);
    };

    let playbackBtnNode = null;
    let overlayPlaybackBtnNode = null;

    if (hasPlaybackEnded) {
        playbackBtnNode = (
            <div className="toggle-btn" onClick={handleVideoReplay}>
                <MaterialSvgIcon iconName="replay" />
            </div>
        );

        overlayPlaybackBtnNode = (
            <div
                className={`overlay-toggle-btn ${overlayButtonVariant}`}
                onClick={handleVideoReplay}
            >
                <MaterialSvgIcon iconName="replay" width={32} height={32} />
            </div>
        );
    } else {
        playbackBtnNode = (
            <div className="toggle-btn" onClick={onPlayPause}>
                <MaterialSvgIcon iconName={playBackIconName} />
            </div>
        );

        if (!isPlaying) {
            overlayPlaybackBtnNode = (
                <div className={`overlay-toggle-btn ${overlayButtonVariant}`} onClick={onPlayPause}>
                    <MaterialSvgIcon iconName={playBackIconName} width={32} height={32} />
                </div>
            );
        }
    }

    let volumeBtnNode = null;
    volumeBtnNode = (
        <div className="toggle-btn" onClick={toggleVolume}>
            <MaterialSvgIcon iconName={!isMuted ? 'volume_on' : 'volume_off'} />
        </div>
    );

    return (
        <div className="l5-custom-player-container">
            <video
                src={src}
                controls={false}
                playsInline
                preload="metadata"
                ref={videoElmRef}
                onClick={onPlayPause}
                poster={poster}
                disablePictureInPicture
                disableRemotePlayback
            />

            {enableControls && (
                <>
                    <div className="overlay-controls">{overlayPlaybackBtnNode}</div>

                    <div
                        className={`video-controls-container  ${!isPlaying ? 'show-controls' : ''}`}
                    >
                        {/* Play / Pause / Replay Button */}
                        {playbackBtnNode}

                        {/* TIMER */}
                        <div className="video-timer">
                            <span>{getFormattedTimer(currentDuration * 1000)}</span>
                        </div>

                        {/* Scrubber */}
                        <div className="video-scrubber">
                            <progress value={currentDuration} max={videoDuration} />
                            <input
                                type="range"
                                name="timestamp"
                                step={seekStep}
                                value={currentDuration}
                                onMouseDown={pauseVideo}
                                onChange={handleSeekVideo}
                                onMouseUp={handleSeekVideo}
                                min={0}
                                max={videoDuration}
                            />
                        </div>

                        {/* Volume Btn */}
                        {volumeBtnNode}

                        {/* Lumen5 Image */}
                        <div className="l5-logo">
                            <a href="https://lumen5.com" target="_blank" rel="noreferrer">
                                <img src="https://storage.googleapis.com/lumen5-site-images/L5-logo/2024/Lumen5-Reverse-White.svg" />
                            </a>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

export default VideoPlayer;
